import PdaTitle from '../../component/common/PdaTitle'
import React, { useContext, useMemo, useState } from 'react'
import { useMountEffect } from 'primereact/hooks'
import { useLocation } from 'react-router-dom'
import { CommonContext } from '../../component/common/context/commonContext'
import { Dropdown } from 'primereact/dropdown'
import AreaSelectDialog from '../../component/location/AreaSelectDialog'
import { LocationSelectData, LocationSaveData, MyLocationTypeData } from '../../types/inventory'
import { isEmpty } from 'lodash'
import SectorSelectDialog from '../../component/location/SectorSelectDialog'
import { CommonCode } from '../../types/common'
import { Button } from 'primereact/button'
import LocationConfirmDialog from '../../component/location/LocationConfirmDialog'
import { useTranslation } from 'react-i18next'
import { tokens } from 'locales/tokens'

const LocationSave = () => {
    const location = useLocation()
    const [areas, setAreas] = useState<LocationSelectData[]>([])
    const [sectors, setSectors] = useState<LocationSelectData[]>([])
    const [bays, setBays] = useState<LocationSelectData[]>([])
    const [rows, setRows] = useState<LocationSelectData[]>([])
    const [cols, setCols] = useState<LocationSelectData[]>([])
    const [lctTypes, setLctTypes] = useState<CommonCode[]>([])
    const [lctOptions, setLctOptions] = useState<CommonCode[]>([])
    const [lctTmprs, setLctTmprs] = useState<CommonCode[]>([])
    const [visibleArea, setVisibleArea] = useState<boolean>(false)
    const [visibleSector, setVisibleSector] = useState<boolean>(false)
    const [visibleConfirm, setVisibleConfirm] = useState<boolean>(false)
    const [disabledTmpr, setDisabledTmpr] = useState<boolean>(false)
    const [height, setHeight] = useState<number>(0)
    const { $codes, $http } = useContext(CommonContext)
    const { t } = useTranslation()

    const [params, setParams] = useState<LocationSaveData>({
        areaId: '',
        sectorId: '',
        bayId: '',
        rackRowId: '',
        rackColId: '',
        lctTypCd: '',
        goodsTmprCd: 'R',
        lctOptionCd: '',
    })

    useMountEffect(() => {
        // 정말 수정이 없을까?
        // findLocationInfo()
        $codes(['LCT_TYP', 'GD_TMP_TYP', 'LCT_OPTION']).then((result: CommonCode[][]) => {
            const [LCT_TYP, GD_TMP_TYP, LCT_OPTION] = result

            setLctTypes(LCT_TYP)
            setLctTmprs(GD_TMP_TYP)
            setLctOptions(LCT_OPTION)
        })

        makeHeight()

        findArea()
    })

    const makeHeight = () => {
        const title = document.getElementsByClassName('pda-title')[0]
        const top = document.getElementsByClassName('location-top')[0]
        const bottom = document.getElementsByClassName('location-bottom')[0]

        setHeight(
            window.innerHeight - title.getBoundingClientRect().height - top.getBoundingClientRect().height - bottom.getBoundingClientRect().height,
        )
    }

    const findMyLocation = async (type: 'Area' | 'Sector' | 'Bay' | 'Row' | 'Col', myLocation?: MyLocationTypeData) => {
        return await $http().get('/inventory/my-location', {
            params: {
                type,
                ...myLocation,
            },
        })
    }

    const findArea = async () => {
        const result = await findMyLocation('Area')
        setAreas(result)
    }

    const findSector = async (areaId: string) => {
        const result = await findMyLocation('Sector', { areaId })
        setSectors(result)
    }

    const findBay = async (sectorId: string) => {
        const result = await findMyLocation('Bay', { areaId: params.areaId, sectorId })
        setBays(result)
    }

    // const findLocationInfo = async () => {
    //     // 현재 로케이션 세팅 정보를 가져온다.
    //     const result = await $http().get(`/inventory/location/${location.state.lctSeq}`)
    //     console.log(result)
    // }

    const onClickArea = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault()
        setVisibleArea(true)
    }

    const onHideArea = (value: string) => {
        if (value) {
            setParams({
                ...params,
                areaId: value,
                sectorId: '', //reset
                bayId: '',
                rackRowId: '',
                rackColId: '',
            })

            findSector(value)
        }

        setVisibleArea(false)
    }

    const onClickSector = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault()
        setVisibleSector(true)
    }

    const onHideSector = (value: string) => {
        if (value) {
            const p = {
                ...params,
                sectorId: value,
                bayId: '', //reset
                rackRowId: '',
                rackColId: '',
            }
            setParams(p)

            findBay(value)

            // 2023-10-06
            findTmprCd(p, value)
        }

        setVisibleSector(false)
    }

    const findTmprCd = async (p: LocationSaveData, sectorId: string) => {
        const { goodsTmprCd } = await $http().get('/inventory/my-temperature', {
            params: {
                areaId: p.areaId,
                sectorId,
            },
        })

        goodsTmprCd ? setDisabledTmpr(true) : setDisabledTmpr(false)
        setParams({
            ...p,
            goodsTmprCd: goodsTmprCd || '',
        })
    }

    const onChangeLocation = async (type: 'bayId' | 'rackRowId' | 'rackColId' | 'lctTypCd' | 'goodsTmprCd' | 'lctOptionCd', value: string) => {
        switch (type) {
            case 'bayId':
                setRows(await findMyLocation('Row', { areaId: params.areaId, sectorId: params.sectorId, bayId: value }))
                break
            case 'rackRowId':
                setCols(await findMyLocation('Col', { areaId: params.areaId, sectorId: params.sectorId, bayId: params.bayId, rackRowId: value }))
                break
        }

        setParams({
            ...params,
            [type]: value,
        })
    }

    const isDisabled = useMemo(
        () =>
            isEmpty(params) ||
            isEmpty(params.areaId) ||
            isEmpty(params.sectorId) ||
            isEmpty(params.bayId) ||
            isEmpty(params.rackRowId) ||
            isEmpty(params.rackColId) ||
            isEmpty(params.lctTypCd) ||
            isEmpty(params.lctOptionCd),
        [params],
    )

    const locationResult = useMemo(() => {
        if (params) {
            let code = '',
                name = ''
            if (params.areaId && areas) {
                const area = areas.filter((area) => area.areaId === params.areaId)[0]
                code = area.areaId
            }

            if (!isEmpty(params.sectorId) && !isEmpty(sectors)) {
                const sector = sectors.filter((sector) => sector.sectorId === params.sectorId)[0]
                if (isEmpty(sector)) {
                    // sectorNm 없다고 에러 터져서..
                    return { name, code }
                }
                name += `${sector.sectorNm}`
                code += `-${sector.sectorId}`
            }

            if (params.bayId) {
                name += `${params.bayId.replaceAll('B', '')}`
                code += `-${params.bayId}`
            }

            if (params.rackRowId) {
                name += ` - ${params.rackRowId?.padStart(2, '0')}`
                code += `-${params.rackRowId}`
            }

            if (params.rackColId) {
                name += ` - ${params.rackColId?.padStart(2, '0')}`
                code += `-${params.rackColId}`
            }

            return { name, code }
        }
    }, [params, areas, sectors])

    const onClickSave = async () => {
        params.goodsTmprCd = 'R'
        const result = await $http().post(`/inventory/location/${location.state.lctSeq}`, {
            ...params,
            lctSeq: location.state.lctSeq,
            lctNo: locationResult?.code,
        })

        if (result) {
            setVisibleConfirm(true)
        }
    }

    const clear = (type: 'SECTOR' | 'AREA') => {
        if (type === 'AREA') {
            setParams({
                ...params,
                sectorId: '',
                bayId: '',
                rackRowId: '',
                rackColId: '',
            })
        } else {
            setParams({
                ...params,
                bayId: '',
                rackRowId: '',
                rackColId: '',
            })
        }
    }
    const onAddLocation = (type: 'SECTOR' | 'AREA') => {
        if (type === 'AREA') {
            findArea()
            clear('AREA')
        } else {
            findSector(params.areaId)
            clear('SECTOR')
        }
    }

    return (
        <>
            <PdaTitle title={t(tokens.inventory.register_location)} historyBack goToHome />
            <div className="m-2 p-2 rdms-bg-color-grey location-top">
                <div className="grid mb-3">
                    <div className="col-12">
                        <span className="font-bold">{t(tokens.inventory.location_label_code)} : </span>
                        <span className="rdms-text-primary ml-2 font-bold">{location.state.lctSeq}</span>
                    </div>
                </div>
                {/* 높이자동, 스크롤 자동 h-auto overflow-y-auto */}
                <div className="grid h-auto overflow-y-auto ">
                    <div className="col-12 grid">
                        <div className="col-7">
                            <div className="font-bold rdms-text-primary required">{t(tokens.common.zone)}(Area)</div>
                            <Dropdown
                                value={params.areaId}
                                className="flex w-full p-inputtext-sm text-sm mt-2"
                                panelClassName="p-inputtext-sm"
                                optionValue="areaId"
                                optionLabel="areaNm"
                                placeholder={t(tokens.inventory.zone_name)}
                                options={areas}
                                onClick={(e) => onClickArea(e)}
                            />
                        </div>
                        <div className="col-5">
                            <div className="font-bold rdms-text-primary">{t(tokens.inventory.zone_code)}</div>
                            <div className="align-self-center mt-3">{params.areaId ? params.areaId : '-'}</div>
                        </div>
                    </div>
                    <div className="col-12 grid">
                        <div className="col-7">
                            <div className="font-bold rdms-text-primary required">{t(tokens.common.sector)}(Sector)</div>
                            <Dropdown
                                value={params.sectorId}
                                className="flex w-full p-inputtext-sm text-sm mt-2"
                                panelClassName="p-inputtext-sm"
                                optionValue="sectorId"
                                optionLabel="sectorNm"
                                placeholder={t(tokens.inventory.sector_name)}
                                options={sectors}
                                onClick={(e) => onClickSector(e)}
                                disabled={isEmpty(params.areaId)}
                            />
                        </div>
                        <div className="col-5">
                            <div className="font-bold rdms-text-primary">{t(tokens.inventory.sector_code)}</div>
                            <div className="align-self-center mt-3">{isEmpty(params.sectorId) ? '-' : params.sectorId}</div>
                        </div>
                    </div>
                    <div className="col-12 grid">
                        <div className="col-7">
                            <div className="font-bold rdms-text-primary required">{t(tokens.inventory.bay)}(Bay)</div>
                            <Dropdown
                                value={params.bayId}
                                className="flex w-full p-inputtext-sm text-sm mt-2"
                                panelClassName="p-inputtext-sm"
                                optionValue="bayId"
                                optionLabel="bayId"
                                placeholder={t(tokens.inventory.bay_name)}
                                options={bays}
                                disabled={isEmpty(params.sectorId)}
                                onChange={(e) => onChangeLocation('bayId', e.value)}
                            />
                        </div>
                        <div className="col-5">
                            <div className="font-bold rdms-text-primary">{t(tokens.inventory.bay_code)}</div>
                            <div className="align-self-center mt-3">{params.bayId ? params.bayId : '-'}</div>
                        </div>
                    </div>
                    <div className="col-12 grid">
                        <div className="col-7">
                            <div className="font-bold rdms-text-primary required">{t(tokens.inventory.rack)}(Rack)</div>
                            <Dropdown
                                value={params.rackRowId}
                                className="flex w-full p-inputtext-sm text-sm mt-2"
                                panelClassName="p-inputtext-sm"
                                optionValue="rackRowId"
                                optionLabel="rackRowId"
                                placeholder={t(tokens.inventory.row_number)}
                                options={rows}
                                disabled={isEmpty(params.bayId)}
                                onChange={(e) => onChangeLocation('rackRowId', e.value)}
                            />
                            <Dropdown
                                value={params.rackColId}
                                className="flex w-full p-inputtext-sm text-sm mt-2"
                                panelClassName="p-inputtext-sm"
                                optionValue="rackColId"
                                optionLabel="rackColId"
                                placeholder={t(tokens.inventory.layer_count)}
                                options={cols}
                                disabled={isEmpty(params.rackRowId)}
                                onChange={(e) => onChangeLocation('rackColId', e.value)}
                            />
                        </div>
                        <div className="col-5">
                            <div className="font-bold rdms-text-primary">{t(tokens.inventory.rack_code)}</div>
                            <div className="align-self-center mt-3">{params.rackRowId ? params.rackRowId : '-'}</div>
                            <div className="align-self-center mt-5">{params.rackColId ? params.rackColId : '-'}</div>
                        </div>
                    </div>
                    <div className="col-12 grid">
                        <div className="col-7">
                            <div className="font-bold rdms-text-primary required">{t(tokens.inventory.location_purpose)}</div>
                            <Dropdown
                                value={params.lctTypCd}
                                className="flex w-full p-inputtext-sm text-sm mt-2"
                                panelClassName="p-inputtext-sm"
                                optionValue="codeId"
                                optionLabel="codeNm"
                                options={lctTypes}
                                placeholder={t(tokens.inventory.location_purpose)}
                                onChange={(e) => onChangeLocation('lctTypCd', e.value)}
                            />
                        </div>
                        {/*<div className="col-5">
                            <div className="font-bold rdms-text-primary required">{t(tokens.inventory.location_temperature)}</div>
                            <Dropdown
                                value={params.goodsTmprCd}
                                className="flex w-full p-inputtext-sm text-sm mt-2"
                                panelClassName="p-inputtext-sm"
                                optionValue="codeId"
                                optionLabel="codeNm"
                                options={lctTmprs}
                                placeholder={t(tokens.inventory.location_temperature)}
                                disabled={disabledTmpr}
                                onChange={(e) => onChangeLocation('goodsTmprCd', e.value)}
                            />
                        </div>*/}
                    </div>
                    <div className="col-12 grid">
                        <div className="col-7">
                            <div className="font-bold rdms-text-primary required">{t(tokens.inventory.location_option)}</div>
                            <Dropdown
                                value={params.lctOptionCd}
                                className="flex w-full p-inputtext-sm text-sm mt-2"
                                panelClassName="p-inputtext-sm"
                                optionValue="codeId"
                                optionLabel="codeNm"
                                options={lctOptions}
                                placeholder={t(tokens.inventory.location_option)}
                                onChange={(e) => onChangeLocation('lctOptionCd', e.value)}
                            />
                        </div>
                    </div>
                    <div className="col-12 grid">
                        <div className="col-12">
                            <div className="p-0">
                                <span className="font-bold">[ {t(tokens.inventory.location_code)} ]</span>
                            </div>
                            <div className="p-0 mt-1">
                                <span className="rdms-text-primary"> {locationResult?.code}</span>
                            </div>
                            <div className="p-0 pt-3">
                                <span className="font-bold">[ {t(tokens.inventory.location_address)} ]</span>
                            </div>
                            <div className="p-0 mt-1">
                                <span className="rdms-text-primary"> {locationResult?.name}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ position: 'fixed', bottom: 0, width: '100%' }} className="location-bottom">
                <Button label={t(tokens.common.register)} className="p-button-success w-full" disabled={isDisabled} onClick={onClickSave} />
            </div>
            {visibleArea && <AreaSelectDialog visible={visibleArea} onHide={onHideArea} areas={areas} onAddLocation={onAddLocation} />}
            {visibleSector && <SectorSelectDialog visible={visibleSector} onHide={onHideSector} sectors={sectors} onAddLocation={onAddLocation} />}
            {visibleConfirm && (
                <LocationConfirmDialog visible={visibleConfirm} onHide={() => setVisibleConfirm(false)} lctSeq={location.state.lctSeq} />
            )}
        </>
    )
}

export default LocationSave
